import {
  Level,
  Tier,
  CharacterTrait,
  ExperienceEntry,
  AdvancementChoice,
  TierAdvancementState,
  LevelUpSession,
  MarkedTraits,
  DomainCardExchange,
  LevelUpChanges,
  getTierForLevel,
  isTierAchievementLevel,
  getUnmarkedTraits,
  getAvailableSlots,
  hasAvailableSlot,
  hasNAvailableSlots,
  getAdvancementSlotCost,
  markNextSlot,
  markNSlots,
  createTierAchievementExperience,
  increaseExperience,
  markTraits,
  clearMarkedTraits
} from '../types/levelUp';
import { CharacterData } from '../types/character';

// ============================================================================
// ADVANCEMENT SLOT CALCULATIONS
// ============================================================================

/**
 * Calculates remaining advancement slots for a session
 */
export function getRemainingSlots(session: LevelUpSession): number {
  const total = 2; // Always 2 slots per level
  const used = session.advancementChoices.reduce(
    (sum, choice) => sum + getAdvancementSlotCost(choice),
    0
  );
  return total - used;
}

/**
 * Checks if a specific advancement can be added to session
 */
export function canAddAdvancement(
  session: LevelUpSession,
  choice: AdvancementChoice
): boolean {
  const remaining = getRemainingSlots(session);
  const cost = getAdvancementSlotCost(choice);
  return remaining >= cost;
}

/**
 * Gets available advancement options for current tier
 */
export interface AvailableAdvancements {
  characterTraits: boolean;
  hitPoints: boolean;
  stress: boolean;
  experience: boolean;
  domainCard: boolean;
  evasion: boolean;
  subclassUpgrade: boolean;
  proficiency: boolean;
  multiclass: boolean;
}

export function getAvailableAdvancements(
  session: LevelUpSession,
  character: CharacterData
): AvailableAdvancements {
  const tierState = session.tierAdvancementState;
  const remaining = getRemainingSlots(session);
  const currentLevel = parseInt(character.level) as Level;
  const targetLevel = session.toLevel; // Level we're leveling UP TO

  return {
    characterTraits:
      remaining >= 1 &&
      hasAvailableSlot(tierState.slots.characterTraits) &&
      getUnmarkedTraits(
        // Use empty marks if this tier achievement clears traits, otherwise use character's marks
        (session.tierAchievement?.traitsCleared)
          ? { agility: false, strength: false, finesse: false, instinct: false, presence: false, knowledge: false }
          : (character.markedTraits || { agility: false, strength: false, finesse: false, instinct: false, presence: false, knowledge: false })
      ).length >= 2,

    hitPoints:
      remaining >= 1 &&
      hasAvailableSlot(tierState.slots.hitPoints),

    stress:
      remaining >= 1 &&
      hasAvailableSlot(tierState.slots.stress),

    experience:
      remaining >= 1 &&
      hasAvailableSlot(tierState.slots.experiences) &&
      // Check both existing experiences AND tier achievement experience from current session
      ((character.experiences?.length || 0) > 0 || session.tierAchievement?.newExperience !== null),

    domainCard:
      remaining >= 1 &&
      hasAvailableSlot(tierState.slots.domainCard),

    evasion:
      remaining >= 1 &&
      hasAvailableSlot(tierState.slots.evasion),

    subclassUpgrade:
      remaining >= 1 &&
      hasAvailableSlot(tierState.slots.subclassUpgrade) &&
      !tierState.crossedOut.subclassUpgrade &&
      !!character.subclass &&
      session.tier >= 3, // Only available in Tier 3 and Tier 4

    proficiency:
      remaining >= 2 &&
      hasNAvailableSlots(tierState.slots.proficiency, 2),

    multiclass:
      remaining >= 2 &&
      hasNAvailableSlots(tierState.slots.multiclass, 2) &&
      !tierState.crossedOut.multiclass &&
      targetLevel >= 5 && // Check target level (level we're going TO)
      !character.multiclass
  };
}

// ============================================================================
// TIER & LEVEL CALCULATIONS
// ============================================================================

/**
 * Calculates what tier a character will be after leveling up
 */
export function getTargetTier(currentLevel: number): Tier {
  const nextLevel = (currentLevel + 1) as Level;
  return getTierForLevel(nextLevel);
}

/**
 * Checks if leveling up will trigger a tier change
 */
export function willTierChange(currentLevel: number): boolean {
  const currentTier = getTierForLevel(currentLevel as Level);
  const nextTier = getTargetTier(currentLevel);
  return nextTier !== currentTier;
}

/**
 * Gets tier achievement bonuses for a level
 */
export function getTierAchievementBonuses(level: Level): {
  newExperience: boolean;
  proficiencyIncrease: boolean;
  clearMarkedTraits: boolean;
} | null {
  if (!isTierAchievementLevel(level)) return null;

  return {
    newExperience: true,
    proficiencyIncrease: true,
    clearMarkedTraits: level === 5 || level === 8
  };
}

// ============================================================================
// EXPERIENCE CALCULATIONS
// ============================================================================

/**
 * Calculates total experience bonus from all experiences
 */
export function calculateTotalExperienceBonus(experiences: ExperienceEntry[]): number {
  return experiences.reduce((total, exp) => total + exp.bonus, 0);
}

/**
 * Gets experiences sorted by bonus value (highest first)
 */
export function getExperiencesSortedByBonus(
  experiences: ExperienceEntry[]
): ExperienceEntry[] {
  return [...experiences].sort((a, b) => b.bonus - a.bonus);
}

/**
 * Gets experiences created at a specific level
 */
export function getExperiencesCreatedAtLevel(
  experiences: ExperienceEntry[],
  level: Level
): ExperienceEntry[] {
  return experiences.filter(exp => exp.createdAtLevel === level);
}

/**
 * Suggests next experience to increase (lowest bonus)
 */
export function suggestExperienceToIncrease(
  experiences: ExperienceEntry[]
): ExperienceEntry | null {
  if (experiences.length === 0) return null;

  return experiences.reduce((lowest, exp) =>
    exp.bonus < lowest.bonus ? exp : lowest
  );
}

// ============================================================================
// CHARACTER TRAIT CALCULATIONS
// ============================================================================

/**
 * Gets count of marked traits
 */
export function getMarkedTraitCount(markedTraits: MarkedTraits): number {
  return Object.values(markedTraits).filter(Boolean).length;
}

/**
 * Suggests traits to increase (unmarked traits with lowest values)
 */
export function suggestTraitsToIncrease(
  character: CharacterData,
  markedTraits: MarkedTraits
): [CharacterTrait, CharacterTrait] | null {
  const unmarked = getUnmarkedTraits(markedTraits);

  if (unmarked.length < 2) return null;

  // Sort unmarked traits by their current value (lowest first)
  const traitValues = character.traits;
  const sorted = unmarked.sort((a, b) => traitValues[a] - traitValues[b]);

  return [sorted[0], sorted[1]];
}

// ============================================================================
// DOMAIN CARD CALCULATIONS
// ============================================================================

/**
 * Gets maximum domain card level for character
 */
export function getMaxDomainCardLevel(
  characterLevel: Level,
  isMulticlassDomain: boolean = false
): number {
  return isMulticlassDomain
    ? Math.ceil(characterLevel / 2)
    : characterLevel;
}

/**
 * Filters domain cards available at character level
 */
export function filterDomainCardsByLevel<T extends { level: number }>(
  cards: T[],
  characterLevel: Level,
  isMulticlassDomain: boolean = false
): T[] {
  const maxLevel = getMaxDomainCardLevel(characterLevel, isMulticlassDomain);
  return cards.filter(card => card.level <= maxLevel);
}

/**
 * Gets current domain card count
 */
export function getDomainCardCount(character: CharacterData): number {
  const loadout = character.inventory?.domainCardLoadout?.length || 0;
  const vault = character.inventory?.domainCardVault?.length || 0;
  return loadout + vault;
}

// ============================================================================
// PROFICIENCY CALCULATIONS
// ============================================================================

/**
 * Calculates proficiency after level-up
 */
export function calculateNewProficiency(
  currentProficiency: number,
  tierAchievement: boolean,
  advancementSelected: boolean
): number {
  let newProf = currentProficiency;

  if (tierAchievement) newProf += 1;
  if (advancementSelected) newProf += 1;

  return Math.min(newProf, 6); // Max proficiency is 6
}

/**
 * Gets proficiency increase sources for a level-up
 */
export function getProficiencyIncreaseSources(session: LevelUpSession): {
  fromTierAchievement: boolean;
  fromAdvancement: boolean;
  totalIncrease: number;
} {
  const fromTierAchievement = session.tierAchievement?.proficiencyIncreased || false;
  const fromAdvancement = session.advancementChoices.some(c => c.type === 'proficiency');

  return {
    fromTierAchievement,
    fromAdvancement,
    totalIncrease: (fromTierAchievement ? 1 : 0) + (fromAdvancement ? 1 : 0)
  };
}

// ============================================================================
// STAT CHANGE CALCULATIONS
// ============================================================================

/**
 * Calculates HP increase from level-up
 */
export function calculateHPIncrease(session: LevelUpSession): number {
  return session.advancementChoices.filter(c => c.type === 'hitPoints').length;
}

/**
 * Calculates Stress increase from level-up
 */
export function calculateStressIncrease(session: LevelUpSession): number {
  return session.advancementChoices.filter(c => c.type === 'stress').length;
}

/**
 * Calculates Evasion increase from level-up
 */
export function calculateEvasionIncrease(session: LevelUpSession): number {
  return session.advancementChoices.filter(c => c.type === 'evasion').length;
}

/**
 * Gets damage threshold increases (always +1 each)
 */
export function getDamageThresholdIncrease(): {
  major: number;
  severe: number;
} {
  return {
    major: 1,
    severe: 1
  };
}

// ============================================================================
// SESSION SUMMARY CALCULATIONS
// ============================================================================

/**
 * Generates a summary of all changes from a level-up session
 */
export function generateLevelUpSummary(
  session: LevelUpSession,
  character: CharacterData
): {
  levelChange: string;
  tierChange: string | null;
  tierAchievements: string[];
  advancements: string[];
  damageThresholds: string;
  domainCard: string;
  proficiencyChange: string | null;
} {
  const fromLevel = session.fromLevel;
  const toLevel = session.toLevel;
  const tierChanged = willTierChange(fromLevel);

  const summary = {
    levelChange: `Level ${fromLevel} → ${toLevel}`,
    tierChange: tierChanged ? `Advanced to Tier ${getTargetTier(fromLevel)}` : null,
    tierAchievements: [] as string[],
    advancements: [] as string[],
    damageThresholds: 'Major +1, Severe +1',
    domainCard: session.newDomainCards && session.newDomainCards.length > 0
      ? `Acquired ${session.newDomainCards.length} new domain card${session.newDomainCards.length > 1 ? 's' : ''}${session.domainCardExchange ? ' (exchanged 1 card)' : ''}`
      : 'None selected',
    proficiencyChange: null as string | null
  };

  // Tier achievements
  if (session.tierAchievement) {
    if (session.tierAchievement.newExperience) {
      summary.tierAchievements.push(
        `New Experience: ${session.tierAchievement.newExperience.name} (+2)`
      );
    }
    if (session.tierAchievement.proficiencyIncreased) {
      summary.tierAchievements.push('Proficiency +1');
    }
    if (session.tierAchievement.traitsCleared) {
      summary.tierAchievements.push('Marked traits cleared');
    }
  }

  // Advancements
  for (const choice of session.advancementChoices) {
    switch (choice.type) {
      case 'characterTraits':
        summary.advancements.push(`${choice.trait1} +1, ${choice.trait2} +1 (marked)`);
        break;
      case 'hitPoints':
        summary.advancements.push('Hit Points +1');
        break;
      case 'stress':
        summary.advancements.push('Stress +1');
        break;
      case 'experience': {
        // Check both character experiences and tier achievement experience
        const exp = character.experiences?.find(e => e.id === choice.experienceId) ||
                    (session.tierAchievement?.newExperience?.id === choice.experienceId
                      ? session.tierAchievement.newExperience
                      : null);
        summary.advancements.push(exp ? `${exp.name} experience +1` : 'Experience +1');
        break;
      }
      case 'domainCard':
        summary.advancements.push('Domain card slot used');
        break;
      case 'evasion':
        summary.advancements.push('Evasion +1');
        break;
      case 'subclassUpgrade':
        summary.advancements.push('Subclass upgraded');
        break;
      case 'proficiency':
        summary.advancements.push('Proficiency +1 (2 slots)');
        break;
      case 'multiclass':
        summary.advancements.push(`Multiclass acquired (2 slots)`);
        break;
    }
  }

  // Proficiency summary
  const profSources = getProficiencyIncreaseSources(session);
  if (profSources.totalIncrease > 0) {
    const sources: string[] = [];
    if (profSources.fromTierAchievement) sources.push('tier achievement');
    if (profSources.fromAdvancement) sources.push('advancement');
    summary.proficiencyChange = `+${profSources.totalIncrease} (from ${sources.join(' and ')})`;
  }

  return summary;
}

/**
 * Calculates completion percentage for wizard steps
 */
export function getWizardCompletionPercentage(session: LevelUpSession): number {
  let completed = 0;
  let total = 4; // 4 main steps

  // Step 1: Tier Achievement (if applicable)
  if (session.tierAchievement) {
    if (session.tierAchievement.newExperience) completed++;
  } else {
    completed++; // Auto-complete if not applicable
  }

  // Step 2: Advancements (2 slots worth)
  const slotsUsed = session.advancementChoices.reduce(
    (sum, choice) => sum + getAdvancementSlotCost(choice),
    0
  );
  if (slotsUsed === 2) completed++;

  // Step 3: Damage Thresholds (automatic)
  if (session.damageThresholdsIncreased) completed++;

  // Step 4: Domain Cards
  const requiredCardCount = session.hasAdditionalDomainCard ? 2 : 1;
  if (session.newDomainCards && session.newDomainCards.length === requiredCardCount) completed++;

  return Math.round((completed / total) * 100);
}

// ============================================================================
// CHANGE APPLICATION CALCULATIONS
// ============================================================================

/**
 * Extracts changes from a completed session for history tracking
 */
export function extractLevelUpChanges(session: LevelUpSession): LevelUpChanges {
  const changes: LevelUpChanges = {
    levelIncreased: true,
    advancements: {},
    damageThresholdsIncreased: session.damageThresholdsIncreased,
    newDomainCard: (session.newDomainCards && session.newDomainCards.length > 0) ? session.newDomainCards[0] : ''
  };

  // Tier achievement changes
  if (session.tierAchievement?.newExperience) {
    changes.tierAchievement = {
      newExperience: session.tierAchievement.newExperience,
      proficiencyIncreased: session.tierAchievement.proficiencyIncreased,
      traitsCleared: session.tierAchievement.traitsCleared
    };
  }

  // Advancement changes
  for (const choice of session.advancementChoices) {
    switch (choice.type) {
      case 'characterTraits':
        changes.advancements.characterTraitsIncreased = {
          trait1: choice.trait1,
          trait2: choice.trait2
        };
        break;
      case 'hitPoints':
        changes.advancements.hitPointsGained =
          (changes.advancements.hitPointsGained || 0) + 1;
        break;
      case 'stress':
        changes.advancements.stressGained =
          (changes.advancements.stressGained || 0) + 1;
        break;
      case 'experience':
        changes.advancements.experienceIncreased = choice.experienceId;
        break;
      case 'domainCard':
        changes.advancements.domainCardAcquired = choice.cardId;
        break;
      case 'evasion':
        changes.advancements.evasionIncreased = true;
        break;
      case 'subclassUpgrade':
        changes.advancements.subclassUpgraded = choice.cardId;
        break;
      case 'proficiency':
        changes.advancements.proficiencyIncreased = true;
        break;
      case 'multiclass':
        changes.advancements.multiclassAcquired = {
          classId: choice.classId,
          domainId: choice.domainId,
          foundationCardId: choice.foundationCardId
        };
        break;
    }
  }

  // Domain card exchange
  if (session.domainCardExchange) {
    changes.domainCardExchanged = session.domainCardExchange;
  }

  return changes;
}
